import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Loader } from "semantic-ui-react";
import { getPayoffQuotelist } from "../../api/BorrowerApi";
import { getMockPayoffQuotelist } from "../../api/mock/MockBorrowerApi";
import { convertToDateString, formatDataProperties, guid, handleErrorResponse } from "../../Helpers";
import { IPayoffQuoteView } from "../interfaces/Borrower";
import { IErrorResponse, IProxyUser } from "../interfaces/Global";
import BCMenuBar from "../ui/BCMenuBar";
import BCMessage from "../ui/BCMessage";
import BCTable from "../ui/BCTable";
import BWExportButton from "./BWExportButton";
import BWSearchBox from "./BWSearchBox";
import "../../styles/borrower/vendor-form.scss";
import BWActionsButton from "./BWActionsButton";
import BCEmptyState from "../ui/BCEmptyState";
import { FeatureFlags } from "../constants/LocalServerConstants";
import { handleApiPromise } from "../../helpers/GlobalHelper";

interface IBWPayoffQuoteRequestAuto {
    isGreaterThanMobile: boolean;
    isGreaterThanTablet: boolean;
    isSidebar: boolean;
    isMessage: boolean;
    setIsMessage: React.Dispatch<React.SetStateAction<boolean>>;
    errorResponse: IErrorResponse | undefined;
    setErrorResponse: React.Dispatch<React.SetStateAction<IErrorResponse | undefined>>
    proxyUser: IProxyUser | undefined;
}

const BWPayoffQuoteRequestAuto = ({ ...props }: IBWPayoffQuoteRequestAuto) => {
    const [isFirstLoad, setIsFirstLoad] = useState(true as boolean);
    const [isLoading, setIsLoading] = useState(true as boolean);
    const [payoffQuoteList, setPayoffQuoteList] = useState([] as IPayoffQuoteView[]);

    const history = useHistory();
    const handleError = useCallback(() => {
        history.push("/error");
    }, [history]);

    const handleToPayoffRequestForm = () => {
        history.push(`/payoffquoterequestauto/${guid()}`);
    };

    const convertToTableFormat = (payoffQuoteList) => {
        const payoffQuoteTable = [] as IPayoffQuoteView[];
        payoffQuoteList.forEach(payoffQuote => payoffQuoteTable.push({
            "loan": payoffQuote.loan,
            "payoffRequestVersionNumber": payoffQuote.payoffRequestVersionNumber,
            "payoffRequestDate": convertToDateString(payoffQuote.payoffRequestDate),
            "validThroughDate": convertToDateString(payoffQuote.validThroughDate),
            "totalPayout": payoffQuote.totalPayout,
            "payoutDate": payoffQuote.payoutDate ? convertToDateString(payoffQuote.payoutDate) : "",
            "loanPropertyUnitReconveyance": payoffQuote.loanPropertyUnitReconveyance,
            "property": payoffQuote.property
        }));

        return formatDataProperties(payoffQuoteTable);
    };

    useEffect(() => {
        const getPayoffQuotelistAysnc = async () => {
            const data = await handleApiPromise(
                () => getPayoffQuotelist(props.proxyUser && props.proxyUser.id),
                () => getMockPayoffQuotelist(),
                (response) => handleErrorResponse(response, props.setIsMessage, props.setErrorResponse)
            ) || [];

            const payoffQuoteViews = [] as IPayoffQuoteView[];
            data.forEach((payoffQuoteListItem) => {
                payoffQuoteViews.push(payoffQuoteListItem as IPayoffQuoteView);
            });

            setPayoffQuoteList(payoffQuoteViews);
            setIsLoading(false);
        };

        if (isFirstLoad) {
            getPayoffQuotelistAysnc();
        }
    }, [props.setIsMessage, props.setErrorResponse, setIsLoading, handleError, props.proxyUser, isFirstLoad]);

    useEffect(() => {
        setIsFirstLoad(false);
    }, []);

    return (
        <div className={(props.isGreaterThanTablet && props.isSidebar) ? "landing-page hide-sidebar" : "landing-page sidebar"}>
            {isLoading ? <Loader active inline="centered" size="big" /> : <React.Fragment>
                <BCMessage
                    animation={"fly down"}
                    duration={750}
                    visible={props.isMessage}
                    transitionOnMount
                    styles="full-width"
                    onDismiss={() => props.setIsMessage(false)}
                    errorResponse={props.errorResponse}
                />
                <BCMenuBar
                    isGreaterThanMobile={props.isGreaterThanMobile}
                    leftChildren={[<BWActionsButton addOptionAction={handleToPayoffRequestForm} />, <BWExportButton />]}
                    rightChildren={[<BWSearchBox />]}
                />
                {payoffQuoteList.length > 0 ? <BCTable
                    dataRules={[{ field: "Id", rule: "hide" }]}
                    data={convertToTableFormat(payoffQuoteList)}
                    selectable
                    overflowX
                    overflowY
                    dataSingleLine
                    sortable={["Loan", "Payoff Request Date", "Valid Through Date", "Total Payout", "Payout Date", "Loan Property Unit Reconveyance", "Property"]}
                    pagination={25}
                /> : <BCEmptyState
                    emptyStateClass="borrower-portal-empty-state"
                    icon="table"
                    text="No pending approval information to display"
                />}
            </React.Fragment>}
            {FeatureFlags.IS_DEBUG && <React.Fragment>
                <button onClick={() => console.log(payoffQuoteList)}>payoffQuoteList</button>
            </React.Fragment>}
        </div>
    );
};

export default BWPayoffQuoteRequestAuto;